/**
 * Achievement System Utilities
 * 
 * Calculates and tracks user achievements based on assessment data
 */

/**
 * Define all available achievements with their criteria
 */
export const ACHIEVEMENT_DEFINITIONS = {
    // Streak Achievements
    firstSteps: {
        id: 'firstSteps',
        name: 'First Steps',
        description: 'Complete your first practice',
        icon: '🎯',
        color: '#17a2b8', // teal
        category: 'streak'
    },
    gettingStarted: {
        id: 'gettingStarted', 
        name: 'Getting Started',
        description: '2 week streak',
        icon: '🔥',
        color: '#fd7e14', // orange
        category: 'streak'
    },
    buildingMomentum: {
        id: 'buildingMomentum',
        name: 'Building Momentum', 
        description: '4 week streak',
        icon: '⚡',
        color: '#ffc107', // yellow
        category: 'streak'
    },
    consistentPerformer: {
        id: 'consistentPerformer',
        name: 'Consistent Performer',
        description: '8 week streak',
        icon: '💪',
        color: '#28a745', // green
        category: 'streak'
    },
    streakMaster: {
        id: 'streakMaster',
        name: 'Streak Master',
        description: '12 week streak',
        icon: '👑',
        color: '#6f42c1', // purple
        category: 'streak'
    },
    legendary: {
        id: 'legendary',
        name: 'Legendary',
        description: '26 week streak',
        icon: '🏆',
        color: '#e83e8c', // pink
        category: 'streak'
    },
    unstoppable: {
        id: 'unstoppable',
        name: 'Unstoppable',
        description: '52 week streak',
        icon: '💎',
        color: '#6610f2', // indigo
        category: 'streak'
    },

    // Volume Achievements
    elevatorExpert: {
        id: 'elevatorExpert',
        name: 'Elevator Expert',
        description: '10 Elevator Pitch assessments',
        icon: '📞',
        color: '#007bff', // blue
        category: 'volume'
    },
    casualConversationalist: {
        id: 'casualConversationalist',
        name: 'Networking Pro',
        description: '10 Casual Encounter assessments', 
        icon: '🤝',
        color: '#6f42c1', // purple (matches your reference image)
        category: 'volume'
    },
    presentationPro: {
        id: 'presentationPro',
        name: 'Presentation Pro',
        description: '10 Sales Presentation assessments',
        icon: '🎤',
        color: '#28a745', // green
        category: 'volume'
    },
    practiceChampion: {
        id: 'practiceChampion',
        name: 'Practice Champion',
        description: '50 total assessments',
        icon: '🏅',
        color: '#fd7e14', // orange
        category: 'volume'
    },
    submissionSuperstar: {
        id: 'submissionSuperstar',
        name: 'Submission Superstar', 
        description: '100 total assessments',
        icon: '⭐',
        color: '#ffc107', // yellow
        category: 'volume'
    },

    // Performance Achievements
    greenLight: {
        id: 'greenLight',
        name: 'Green Light',
        description: 'Get your first Green grade',
        icon: '✅',
        color: '#28a745', // green
        category: 'performance'
    },
    excellenceSeeker: {
        id: 'excellenceSeeker',
        name: 'Excellence Seeker',
        description: 'Get 5 Green grades',
        icon: '🌟',
        color: '#ffc107', // yellow
        category: 'performance'
    },
    perfectWeek: {
        id: 'perfectWeek',
        name: 'Perfect Week',
        description: 'Complete all 3 assessment types in one week',
        icon: '📅',
        color: '#17a2b8', // teal
        category: 'performance'
    },
    interactiveMaster: {
        id: 'interactiveMaster',
        name: 'Interactive Master',
        description: '10 Interactive mode assessments',
        icon: '🎮',
        color: '#e83e8c', // pink
        category: 'performance'
    },

    // Progress Achievements
    coffeeChat: {
        id: 'coffeeChat',
        name: 'First Connection',
        description: 'Complete first Casual Encounter',
        icon: '👋',
        color: '#6f42c1', // purple
        category: 'progress'
    },
    wellRounded: {
        id: 'wellRounded',
        name: 'Well Rounded',
        // Description matches the check (5 core types). Widening the CHECK to
        // literally every type would retroactively un-earn it for users.
        description: 'Complete the five core session types at least once',
        icon: '🌐',
        color: '#20c997', // teal-green
        category: 'progress'
    },
    murphysFriend: {
        id: 'murphysFriend',
        name: "Murphy's Friend",
        description: 'Respond to Murphy in 10 interactive sessions without ignoring',
        icon: '🤖',
        color: '#007bff', // blue
        category: 'performance'
    },
    earlyRiser: {
        id: 'earlyRiser',
        name: 'Early Riser',
        description: 'Complete a practice before 8 AM',
        icon: '🌅',
        color: '#fd7e14', // orange
        category: 'progress'
    },
    powerHour: {
        id: 'powerHour',
        name: 'Power Hour',
        description: 'Complete 3 practices in one day',
        icon: '⚡',
        color: '#e83e8c', // pink
        category: 'performance'
    },
    dealCloser: {
        id: 'dealCloser',
        name: 'Deal Closer',
        description: '5 Document Defense sessions',
        icon: '📋',
        color: '#6f42c1', // purple
        category: 'volume'
    }
};

/**
 * Calculate which achievements a user has earned
 * @param {Array} assessments - User's completed assessments
 * @param {Object} streakData - User's streak calculation data
 * @returns {Object} - Object with earned and available achievements
 */
export const calculateUserAchievements = (assessments, streakData) => {
    if (!streakData) {
        streakData = { totalSubmissions: 0, longestStreak: 0, currentStreak: 0 };
    }
    const earned = [];
    const available = [];

    // Helper functions
    const hasSessionType = (sessionType, count = 1) => {
        return assessments.filter(a => a.sessionType === sessionType).length >= count;
    };

    const hasGreenGrades = (count = 1) => {
        return assessments.filter(a => a.grade === 'green').length >= count;
    };

    const hasInteractiveAssessments = (count = 1) => {
        // Level 2 interactive assessments have murphyInteractionStatus set
        return assessments.filter(a =>
            a.murphyInteractionStatus === 'responded' ||
            a.murphyInteractionStatus === 'ignored'
        ).length >= count;
    };

    const hasPerfectWeek = () => {
        // Group assessments by week and check if any week has all 3 session types
        const weeklyAssessments = new Map();
        
        assessments.forEach(assessment => {
            const date = new Date(assessment.completedDateTime);
            // One malformed timestamp would make toISOString() throw RangeError
            // and white-screen the dashboard — skip bad records instead.
            if (isNaN(date.getTime())) return;
            const weekStart = getWeekStart(date);
            const weekKey = weekStart.toISOString().split('T')[0];

            if (!weeklyAssessments.has(weekKey)) {
                weeklyAssessments.set(weekKey, new Set());
            }

            weeklyAssessments.get(weekKey).add(assessment.sessionType);
        });
        
        // Check if any week has all 3 main session types
        for (const sessionTypes of weeklyAssessments.values()) {
            if (sessionTypes.has('Elevator Pitch') && 
                sessionTypes.has('Casual Encounter') && 
                (sessionTypes.has('Sales Presentation') || sessionTypes.has('Sales Presentation / Demo'))) {
                return true;
            }
        }
        return false;
    };

    // Get week start (Monday, UTC-based)
    const getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getUTCDay();
        const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
        d.setUTCDate(diff);
        d.setUTCHours(0, 0, 0, 0);
        return d;
    };

    // Check each achievement
    Object.values(ACHIEVEMENT_DEFINITIONS).forEach(achievement => {
        let isEarned = false;

        switch (achievement.id) {
            // Streak achievements
            case 'firstSteps':
                isEarned = streakData.totalSubmissions >= 1;
                break;
            case 'gettingStarted':
                isEarned = streakData.longestStreak >= 2;
                break;
            case 'buildingMomentum':
                isEarned = streakData.longestStreak >= 4;
                break;
            case 'consistentPerformer':
                isEarned = streakData.longestStreak >= 8;
                break;
            case 'streakMaster':
                isEarned = streakData.longestStreak >= 12;
                break;
            case 'legendary':
                isEarned = streakData.longestStreak >= 26;
                break;
            case 'unstoppable':
                isEarned = streakData.longestStreak >= 52;
                break;

            // Volume achievements
            case 'elevatorExpert':
                isEarned = hasSessionType('Elevator Pitch', 10);
                break;
            case 'casualConversationalist':
                isEarned = hasSessionType('Casual Encounter', 10);
                break;
            case 'presentationPro':
                isEarned = assessments.filter(a => a.sessionType === 'Sales Presentation' || a.sessionType === 'Sales Presentation / Demo').length >= 10;
                break;
            case 'practiceChampion':
                isEarned = streakData.totalSubmissions >= 50;
                break;
            case 'submissionSuperstar':
                isEarned = streakData.totalSubmissions >= 100;
                break;

            // Performance achievements
            case 'greenLight':
                isEarned = hasGreenGrades(1);
                break;
            case 'excellenceSeeker':
                isEarned = hasGreenGrades(5);
                break;
            case 'perfectWeek':
                isEarned = hasPerfectWeek();
                break;
            case 'interactiveMaster':
                isEarned = hasInteractiveAssessments(10);
                break;

            // Progress achievements
            case 'coffeeChat':
                isEarned = hasSessionType('Casual Encounter', 1);
                break;
            case 'wellRounded':
                isEarned = hasSessionType('Elevator Pitch', 1) &&
                    hasSessionType('Casual Encounter', 1) &&
                    (hasSessionType('Sales Presentation', 1) || hasSessionType('Sales Presentation / Demo', 1)) &&
                    hasSessionType('Objection Handling', 1) &&
                    hasSessionType('Document Defense', 1);
                break;
            case 'murphysFriend':
                isEarned = assessments.filter(a => a.murphyInteractionStatus === 'responded').length >= 10;
                break;
            // earlyRiser/powerHour deliberately use LOCAL time: "before 8 AM"
            // and "3 in one day" describe the user's own morning/day (like the
            // Training Plan's local-day buckets), unlike the week-based stats,
            // which are UTC to match streakCalculation.
            case 'earlyRiser':
                isEarned = assessments.some(a => {
                    if (!a.completedDateTime) return false;
                    const d = new Date(a.completedDateTime);
                    return !isNaN(d.getTime()) && d.getHours() < 8;
                });
                break;
            case 'powerHour':
                isEarned = (() => {
                    const dailyCounts = new Map();
                    assessments.forEach(a => {
                        if (!a.completedDateTime) return;
                        const d = new Date(a.completedDateTime);
                        if (isNaN(d.getTime())) return;
                        const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                        dailyCounts.set(dayKey, (dailyCounts.get(dayKey) || 0) + 1);
                    });
                    return Array.from(dailyCounts.values()).some(count => count >= 3);
                })();
                break;
            case 'dealCloser':
                isEarned = hasSessionType('Document Defense', 5);
                break;

            default:
                isEarned = false;
        }

        if (isEarned) {
            earned.push(achievement);
        } else {
            available.push(achievement);
        }
    });

    return {
        earned: earned.sort((a, b) => a.name.localeCompare(b.name)),
        available: available.sort((a, b) => a.name.localeCompare(b.name)),
        totalEarned: earned.length,
        totalAvailable: available.length
    };
};